import type { CookieConsentConfig } from 'vanilla-cookieconsent'
import * as CookieConsent from 'vanilla-cookieconsent'

import 'vanilla-cookieconsent/dist/cookieconsent.css'

import {
  CAT_ADVERTISEMENT,
  CAT_ANALYTICS,
  CAT_FUNCTIONALITY,
  CAT_NECESSARY,
  CAT_SECURITY,
  SERVICE_AD_PERSONALIZATION,
  SERVICE_AD_STORAGE,
  SERVICE_AD_USER_DATA,
  SERVICE_ANALYTICS_STORAGE,
  SERVICE_FUNCTIONALITY_STORAGE,
  SERVICE_PERSONALIZATION_STORAGE,
  SERVICE_SECURITY_STORAGE,
} from '../gtag.constant'

export default defineNuxtPlugin(() => {
  const { gtag, initialize } = useGtag()
  const router = useRouter()

  const cookieConsent = ref(CookieConsent)

  function updateGtagConsent() {
    const data = {
      [SERVICE_ANALYTICS_STORAGE]: cookieConsent.value.acceptedService(SERVICE_ANALYTICS_STORAGE, CAT_ANALYTICS),
      [SERVICE_AD_STORAGE]: cookieConsent.value.acceptedService(SERVICE_AD_STORAGE, CAT_ADVERTISEMENT),
      [SERVICE_AD_USER_DATA]: cookieConsent.value.acceptedService(SERVICE_AD_USER_DATA, CAT_ADVERTISEMENT),
      [SERVICE_AD_PERSONALIZATION]: cookieConsent.value.acceptedService(SERVICE_AD_PERSONALIZATION, CAT_ADVERTISEMENT),
      [SERVICE_FUNCTIONALITY_STORAGE]: cookieConsent.value.acceptedService(SERVICE_FUNCTIONALITY_STORAGE, CAT_FUNCTIONALITY),
      [SERVICE_PERSONALIZATION_STORAGE]: cookieConsent.value.acceptedService(SERVICE_PERSONALIZATION_STORAGE, CAT_FUNCTIONALITY),
      [SERVICE_SECURITY_STORAGE]: cookieConsent.value.acceptedService(SERVICE_SECURITY_STORAGE, CAT_SECURITY),
    }
    if (Object.values(data).some((v) => v === true)) {
      initialize()
    }
    gtag('consent', 'update', {
      // Analyse
      [SERVICE_ANALYTICS_STORAGE]: data[SERVICE_ANALYTICS_STORAGE] ? 'granted' : 'denied',
      // Publicité
      [SERVICE_AD_STORAGE]: data[SERVICE_AD_STORAGE] ? 'granted' : 'denied',
      [SERVICE_AD_USER_DATA]: data[SERVICE_AD_USER_DATA] ? 'granted' : 'denied',
      [SERVICE_AD_PERSONALIZATION]: data[SERVICE_AD_PERSONALIZATION] ? 'granted' : 'denied',
      // Fonctionnalité
      [SERVICE_FUNCTIONALITY_STORAGE]: data[SERVICE_FUNCTIONALITY_STORAGE] ? 'granted' : 'denied',
      [SERVICE_PERSONALIZATION_STORAGE]: data[SERVICE_PERSONALIZATION_STORAGE] ? 'granted' : 'denied',
      // Sécurité
      [SERVICE_SECURITY_STORAGE]: data[SERVICE_SECURITY_STORAGE] ? 'granted' : 'denied',
    })
  }

  const config: CookieConsentConfig = {
  // Déclencher la mise à jour du consentement lorsque les choix de l'utilisateur changent
    onFirstConsent: () => {
      updateGtagConsent()
    },
    onConsent: () => {
      updateGtagConsent()
    },
    onChange: () => {
      updateGtagConsent()
    },

    root: '#cc-container', // Important, generate the html inside a common/persistent element

    // Configurer les catégories et services
    categories: {
      [CAT_NECESSARY]: {
        enabled: true, // cette catégorie est activée par défaut
        readOnly: true, // cette catégorie ne peut pas être désactivée
      },
      [CAT_ANALYTICS]: {
        autoClear: {
          cookies: [
            {
              name: /^_ga/, // regex : correspond à tous les cookies commençant par '_ga'
            },
            {
              name: '_gid', // string : nom exact du cookie
            },
          ],
        },
        services: {
          [SERVICE_ANALYTICS_STORAGE]: {
            label: 'Active le stockage (comme les cookies) lié aux analyses, par ex. la durée de visite.',
          },
        },
      },
      [CAT_ADVERTISEMENT]: {
        services: {
          [SERVICE_AD_STORAGE]: {
            label: 'Active le stockage (comme les cookies) lié à la publicité.',
          },
          [SERVICE_AD_USER_DATA]: {
            label: 'Définit le consentement pour l\'envoi de données utilisateur liées à la publicité à Google.',
          },
          [SERVICE_AD_PERSONALIZATION]: {
            label: 'Définit le consentement pour la publicité personnalisée.',
          },
        },
      },
      [CAT_FUNCTIONALITY]: {
        services: {
          [SERVICE_FUNCTIONALITY_STORAGE]: {
            label: 'Active le stockage prenant en charge les fonctionnalités du site web ou de l\'application, par ex. les paramètres de langue.',
          },
          [SERVICE_PERSONALIZATION_STORAGE]: {
            label: 'Active le stockage lié à la personnalisation, par ex. recommandations de vidéos.',
          },
        },
      },
      [CAT_SECURITY]: {
        services: {
          [SERVICE_SECURITY_STORAGE]: {
            label: 'Active le stockage lié à la sécurité, comme l\'authentification, la prévention de la fraude et la protection des utilisateurs.',
          },
        },
      },
    },

    language: {
      default: 'fr',
      translations: {
        fr: {
          consentModal: {
            title: 'Nous utilisons des cookies',
            description: 'La société BOOKANDGOLF et ses partenaires utilisent des cookies indispensables au bon fonctionnement du site, non soumis à votre consentement, ainsi que des cookies pour améliorer votre expérience lorsque vous naviguez sur le site et, avec votre consentement, pour vous proposer du contenu et/ou des publicités personnalisées en fonction de votre navigation ou de votre localisation. Vous pouvez accepter, refuser ou gérer vos préférences en cliquant ci-dessous ou à tout moment en cliquant sur le bouton dédié. Pour en savoir plus sur les cookies, et les caractéristiques des traitements réalisés, vous pouvez consulter notre <a href="/privacy-policy">Politique de confidentialité</a>.',
            acceptAllBtn: 'Tout accepter',
            acceptNecessaryBtn: 'Tout refuser',
            showPreferencesBtn: 'Gérer les préférences individuelles',
          },
          preferencesModal: {
            title: 'Gérer les préférences des cookies',
            acceptAllBtn: 'Tout accepter',
            acceptNecessaryBtn: 'Tout refuser',
            savePreferencesBtn: 'Accepter la sélection actuelle',
            closeIconLabel: 'Fermer la fenêtre',
            sections: [
              {
                title: 'Utilisation des cookies',
                description: 'Nous utilisons des cookies pour garantir les fonctionnalités de base du site web et améliorer votre expérience en ligne.',
              },
              {
                title: 'Cookies strictement nécessaires',
                description: 'Ces cookies sont essentiels au bon fonctionnement du site web, par exemple pour l\'authentification des utilisateurs.',
                linkedCategory: CAT_NECESSARY,
              },
              {
                title: 'Analyse',
                description: 'Les cookies utilisés pour l\'analyse aident à collecter des données permettant aux services de comprendre comment les utilisateurs interagissent avec un service particulier. Ces informations permettent d\'améliorer le contenu et de développer de meilleures fonctionnalités.<br><a href="https://business.safety.google/privacy/" target="_blank" rel="noopener noreferrer">Voir les conditions d\'utilisation Google</a>',
                linkedCategory: CAT_ANALYTICS,
                cookieTable: {
                  headers: {
                    name: 'Nom',
                    domain: 'Service',
                    description: 'Description',
                    expiration: 'Expiration',
                  },
                  body: [
                    {
                      name: '_ga',
                      domain: 'Google Analytics',
                      description: 'Cookie défini par <a href="https://business.safety.google/adscookies/" target="_blank" rel="noopener noreferrer">Google Analytics</a>',
                      expiration: 'Expire après 12 jours',
                    },
                    {
                      name: '_gid',
                      domain: 'Google Analytics',
                      description: 'Cookie défini par <a href="https://business.safety.google/adscookies/" target="_blank" rel="noopener noreferrer">Google Analytics</a>',
                      expiration: 'Session',
                    },
                  ],
                },
              },
              {
                title: 'Publicité',
                description: 'Google utilise des cookies pour la publicité, y compris l\'affichage et le rendu des annonces, la personnalisation des publicités (selon vos paramètres sur <a href="https://g.co/adsettings" target="_blank" rel="noopener noreferrer">g.co/adsettings</a>), la limitation du nombre d\'affichages d\'une publicité, la suppression des annonces que vous ne souhaitez plus voir et la mesure de leur efficacité.<br><a href="https://business.safety.google/privacy/" target="_blank" rel="noopener noreferrer">Voir les conditions d\'utilisation Google</a>',
                linkedCategory: CAT_ADVERTISEMENT,
              },
              {
                title: 'Fonctionnalité',
                description: 'Les cookies de fonctionnalité permettent aux utilisateurs d\'interagir avec un service ou un site pour accéder à des fonctionnalités fondamentales, comme les préférences linguistiques et les optimisations du produit.',
                linkedCategory: CAT_FUNCTIONALITY,
              },
              {
                title: 'Sécurité',
                description: 'Les cookies de sécurité authentifient les utilisateurs, préviennent la fraude et protègent les utilisateurs lorsqu\'ils interagissent avec un service.',
                linkedCategory: CAT_SECURITY,
              },
              {
                title: 'Plus d\'informations',
                description: `Pour toute question relative à la politique des cookies et à vos choix, veuillez <a href="javascript:void(0)" onclick="window.contact()">nous contacter</a>.`,
              },
            ],
          },
        },
      },
    },
  }
  cookieConsent.value.run(config)

  window.contact = () => {
    router.push('/?modal=contact')
    cookieConsent.value.hidePreferences()
  }

  return {
    provide: {
      cookie: cookieConsent.value,
    },
  }
})
